/**
 * The channel's current-broadcast donations, as a section. Loads on mount /
 * channel change via `/donations?channelId=`, then stays live off the same
 * `queue.updated` WebSocket push the Queue tab uses — donation links, notes,
 * and new donations all go through queue-mutation ops on the backend, so the
 * nudge covers this tab too.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type DonationDto, type RequestDto } from "../lib/api";
import { config } from "../lib/config";
import { AddPanel } from "./AddPanel";
import { DonationRow, type LinkTarget } from "./DonationRow";
import { DonationsSkeleton } from "./Skeleton";

const RECONNECT_MS = 2000;

/** Clear presets → cutoff. "Midnight" is the viewer's local previous midnight. */
const CLEAR_PRESETS: { label: string; cutoff: () => Date }[] = [
  {
    label: "Before last midnight",
    cutoff: () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    },
  },
  { label: "Older than a week", cutoff: () => new Date(Date.now() - 7 * 86_400_000) },
  {
    label: "Older than a month",
    cutoff: () => {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      return d;
    },
  },
];

/** Free requests from this donor — the only sane targets for a manual re-match. */
function targetsFor(d: DonationDto, free: RequestDto[]): LinkTarget[] {
  return free
    .filter(
      (r) =>
        r.requester.platform === d.platform &&
        r.requester.username.toLowerCase() === d.donorUsername.toLowerCase(),
    )
    .map((r) => ({
      id: r.id,
      youtubeUrl: r.youtubeUrl,
      chatTitle: r.title,
      matchedCents: r.matchedCents,
    }));
}

export function DonationSection({
  channelId,
  label,
}: {
  channelId: string;
  label: string;
}) {
  const [items, setItems] = useState<DonationDto[] | null>(null);
  const [freeRequests, setFreeRequests] = useState<RequestDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  async function refreshFreeRequests() {
    try {
      const res = await api.queue(channelId);
      setFreeRequests(res.free);
    } catch {
      /* the picker just won't offer targets — not worth surfacing as an error */
    }
  }

  const refreshDonations = useCallback(async () => {
    if (!channelId) return;
    try {
      const res = await api.donations(channelId);
      // Newest-first, matching the Completed queue.
      setItems([...res.items].sort((a, b) => b.donatedAt.localeCompare(a.donatedAt)));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [channelId]);

  async function editNotes(d: DonationDto, notes: string) {
    setBusyId(d.id);
    setOpError(null);
    try {
      const res = await api.queueOp({
        channelId,
        op: "setDonationNotes",
        donatedAt: d.donatedAt,
        donationId: d.id,
        notes,
      });
      if (res.donation) {
        const updated = res.donation;
        setItems((prev) => prev?.map((item) => (item.id === d.id ? updated : item)) ?? prev);
      }
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function unlink(d: DonationDto) {
    setBusyId(d.id);
    setOpError(null);
    try {
      await api.queueOp({
        channelId,
        op: "unlink",
        donatedAt: d.donatedAt,
        donationId: d.id,
      });
      // The op returns the affected request, not the donation — refetch so the
      // row reflects its new credit state.
      await refreshDonations();
      await refreshFreeRequests();
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function deleteDonation(d: DonationDto) {
    if (!window.confirm("Delete this donation? This can't be undone.")) return;
    setBusyId(d.id);
    setOpError(null);
    try {
      await api.queueOp({
        channelId,
        op: "removeDonation",
        donatedAt: d.donatedAt,
        donationId: d.id,
      });
      setItems((prev) => prev?.filter((item) => item.id !== d.id) ?? prev);
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function clearOld(preset: (typeof CLEAR_PRESETS)[number]) {
    const before = preset.cutoff();
    const count = items?.filter((d) => d.donatedAt < before.toISOString()).length ?? 0;
    setClearOpen(false);
    if (count === 0) {
      setOpError("No donations match that range.");
      return;
    }
    if (
      !window.confirm(
        `Clear ${count} donation${count === 1 ? "" : "s"} from before ${before.toLocaleString()}? ` +
          "They're hidden from this list; request matching and credit aren't affected.",
      )
    ) {
      return;
    }
    setClearing(true);
    setOpError(null);
    try {
      await api.queueOp({ channelId, op: "clearDonations", before: before.toISOString() });
      await refreshDonations();
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setClearing(false);
    }
  }

  async function link(d: DonationDto, requestId: string) {
    setBusyId(d.id);
    setOpError(null);
    try {
      await api.queueOp({
        channelId,
        op: "link",
        donatedAt: d.donatedAt,
        donationId: d.id,
        requestId,
      });
      await refreshDonations();
      await refreshFreeRequests();
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  useEffect(() => {
    setItems(null);
    setError(null);
    void refreshDonations();
    void refreshFreeRequests();
  }, [channelId]);

  // WebSocket subscription, re-established if the socket drops — same
  // `queue.updated` nudge the Queue tab listens for.
  const socketRef = useRef<WebSocket | null>(null);
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(
        `${config.wsUrl}?channelId=${encodeURIComponent(channelId)}`,
      );
      socketRef.current = ws;

      ws.onopen = () => !cancelled && setLive(true);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as { type?: string };
          if (msg.type === "queue.updated") {
            void refreshDonations();
            void refreshFreeRequests();
          }
        } catch {
          /* ignore non-JSON frames */
        }
      };
      ws.onclose = () => {
        if (cancelled) return;
        setLive(false);
        retry = setTimeout(connect, RECONNECT_MS);
      };
      // `error` is always followed by `close`, which owns the reconnect.
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      socketRef.current?.close();
    };
  }, [channelId, refreshDonations]);

  const total = items?.reduce((sum, d) => sum + d.amountCents, 0) ?? 0;

  return (
    <section className="section donation-section">
      <div className="section-head">
        <h2>{label}</h2>
        {items && <span className="count">{items.length}</span>}
        <span className={`live ${live ? "on" : ""}`}>
          <span className="dot" />
          {live ? "live" : "reconnecting…"}
        </span>
        <div className="clear-menu">
          <button
            type="button"
            className="btn ghost"
            disabled={clearing || !items?.length}
            aria-expanded={clearOpen}
            onClick={() => setClearOpen((o) => !o)}
          >
            {clearing ? "Clearing…" : "Clear old…"}
          </button>
          {clearOpen && (
            <div className="clear-menu-pop" role="menu">
              {CLEAR_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  role="menuitem"
                  className="btn ghost"
                  onClick={() => void clearOld(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div className="error">Couldn’t load donations — {error}</div>}
      {opError && <div className="error">{opError}</div>}

      {error ? null : !items ? (
        <DonationsSkeleton />
      ) : items.length === 0 ? (
        <div className="empty">No donations on this stream.</div>
      ) : (
        <>
          {items.map((d) => (
            <DonationRow
              key={d.id}
              donation={d}
              busy={busyId === d.id}
              onEditNotes={(notes) => editNotes(d, notes)}
              onUnlink={() => unlink(d)}
              onDelete={() => deleteDonation(d)}
              linkTargets={targetsFor(d, freeRequests)}
              onLink={(requestId) => link(d, requestId)}
            />
          ))}
          <div className="section-total">
            Total <strong>${(total / 100).toFixed(2)}</strong>
          </div>
        </>
      )}

      <button
        type="button"
        className="fab"
        onClick={() => setAddOpen(true)}
        aria-label="Add donation"
        title="Add donation"
      >
        +
      </button>

      {addOpen && (
        <AddPanel
          channelId={channelId}
          onChanged={() => {
            void refreshDonations();
            void refreshFreeRequests();
          }}
          onClose={() => setAddOpen(false)}
        />
      )}
    </section>
  );
}
